import { useState, useEffect } from "react";
import { Users, Plus, Trash2, CheckCircle, AlertCircle, X, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminsTab({ token, currentUser }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [naam, setNaam] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/company/admins`, auth)
      .then(r => r.json())
      .then(d => { setAdmins(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!naam || !email || !password) { setError("Vul alle velden in"); return; }
    if (password.length < 8) { setError("Wachtwoord minimaal 8 tekens"); return; }
    setSaving(true); setError("");
    try {
      const r = await fetch(`${API}/api/company/admins`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth.headers },
        body: JSON.stringify({ naam, email, password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Toevoegen mislukt");
      setSuccess(`${naam} is toegevoegd als beheerder.`);
      setNaam(""); setEmail(""); setPassword("");
      setShowForm(false);
      load();
      setTimeout(() => setSuccess(""), 4000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleRemove = async (id, adminNaam) => {
    if (!window.confirm(`Beheerder "${adminNaam}" verwijderen?`)) return;
    try {
      const r = await fetch(`${API}/api/company/admins/${id}`, { method: "DELETE", ...auth });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail); }
      load();
    } catch (err) { setError(err.message); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-5 h-5 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-5 max-w-xl">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Configuratie</p>
          <h2 className="text-xl font-bold text-white">Beheerders</h2>
          <p className="text-zinc-500 text-sm mt-1">
            Voeg extra beheerders toe die toegang krijgen tot dit portaal.
          </p>
        </div>
        <button
          onClick={() => { setShowForm(f => !f); setError(""); }}
          data-testid="add-admin-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#C9A84C] text-black text-sm font-bold hover:bg-[#d4b05c] transition-all"
        >
          <Plus size={14} /> Beheerder toevoegen
        </button>
      </div>

      {success && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
          <CheckCircle size={14} /> {success}
        </div>
      )}

      {/* Formulier */}
      {showForm && (
        <form
          onSubmit={handleAdd}
          className="bg-[#111] border border-[#C9A84C]/25 rounded-2xl p-5 space-y-4 animate-fadeIn"
        >
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-white font-semibold text-sm">Nieuwe beheerder</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white">
              <X size={16} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Naam</span>
              <input
                value={naam}
                onChange={e => setNaam(e.target.value)}
                placeholder="Voornaam Achternaam"
                className="bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">E-mail</span>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="naam@bedrijf.nl"
                className="bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Wachtwoord</span>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Minimaal 8 tekens"
                className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-2.5 pr-10 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              >
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </label>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle size={13} /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            data-testid="save-admin-btn"
            className="w-full py-3 rounded-xl bg-[#C9A84C] text-black font-bold text-sm hover:bg-[#d4b05c] disabled:opacity-60 transition-all"
          >
            {saving ? "Toevoegen..." : "Beheerder toevoegen"}
          </button>
          <p className="text-zinc-600 text-xs text-center">
            De nieuwe beheerder ontvangt een welkomstmail met toegangslink.
          </p>
        </form>
      )}

      {/* Lijst */}
      <div className="bg-[#111] border border-white/8 rounded-2xl overflow-hidden">
        {admins.length === 0 ? (
          <div className="text-center py-10 text-zinc-600">
            <Users size={24} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nog geen extra beheerders</p>
          </div>
        ) : (
          <ul className="divide-y divide-white/4">
            {admins.map(a => (
              <li key={a.id} className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/25 flex items-center justify-center text-[#C9A84C] text-xs font-bold">
                    {a.naam?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">
                      {a.naam}
                      {a.is_zichzelf && (
                        <span className="ml-2 text-xs text-zinc-500">(jij)</span>
                      )}
                    </p>
                    <p className="text-zinc-500 text-xs">{a.email}</p>
                  </div>
                </div>
                {!a.is_zichzelf && (
                  <button
                    onClick={() => handleRemove(a.id, a.naam)}
                    data-testid={`remove-admin-${a.id}`}
                    className="text-zinc-600 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-500/10"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
